import LinkButton from '@carvana/ds-link-button';
import React from 'react';
import { StepName } from '|/app/services';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import useNewCar from '|/hooks/use-new-car';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import deliveryLocationImage from './assets/delivery-location.jpg';
import pickupLocationImage from './assets/pickup-location.jpg';
import styles from './getting-vehicle.module.css';
import useOrderSummaryVm from '../../use-order-summary-vm';

interface GettingVehicleProps {
  summary?: ReviewSummaryResponse;
  variant?: string;
  testId?: string;
  vehicleType?: string;
}

const GettingVehicle = ({
  summary = {} as ReviewSummaryResponse,
  variant = '',
  testId = 'getting-vehicle-section',
  vehicleType: vehicleTypeProp
}: GettingVehicleProps) => {
  const navigateToStep = useNavigateToStep();
  const { getDealershipDisplayName } = useNewCar();
  const {
    hasTransferMethod,
    hasTransferMethod: showEdit,
    isVendingMachine,
    isDelivery,
    isPickup,
    transferAddress,
    vehicleType: vehicleTypeFromVm
  } = useOrderSummaryVm(summary);

  if (!hasTransferMethod) return null;

  const vehicleType = vehicleTypeProp ?? vehicleTypeFromVm;

  const isNewCarDealership = vehicleType?.toLowerCase() === 'new';

  const addressLabel = isDelivery ? 'Delivering to' : 'Picking up from';
  const locationImage = isDelivery ? deliveryLocationImage : pickupLocationImage;
  const locationNameDisplay = isVendingMachine
    ? `${transferAddress?.city} Vending Machine`
    : isNewCarDealership
      ? getDealershipDisplayName(transferAddress?.city ?? '', transferAddress?.city)
      : 'Carvana pickup location';

  return (
    <section data-testid={testId}>
      <h3>Getting your vehicle</h3>

      {hasTransferMethod && transferAddress && (
        <div className={styles.addressSection}>
          {variant !== 'sidebar' && (
            <img src={locationImage} alt={addressLabel} className={styles.addressImage} />
          )}
          <div className={styles.addressContent}>
            <h4 className={styles.addressLabel}>{addressLabel}</h4>
            <div className={styles.addressText}>
              {isPickup && <p className={styles.locationName}>{locationNameDisplay}</p>}
              {transferAddress.line1} {transferAddress.line2 && transferAddress.line2}
              <br />
              {transferAddress.city}, {transferAddress.state} {transferAddress.zipCode}
            </div>
          </div>
        </div>
      )}

      <div className={styles.schedulingMessage}>
        You&apos;ll pick the specific date and time for your {isDelivery ? 'delivery' : 'pickup'}{' '}
        after completing verification.
      </div>

      {showEdit && (
        <LinkButton
          className={styles.editButton}
          size="sm"
          underline={false}
          onClick={() => {
            navigateToStep(StepName.PickupDelivery);
          }}
          data-analytics-id="pp_review_order_summary_edit_pickup_delivery"
          data-testid="getting-vehicle-edit-button"
        >
          Edit {isDelivery ? 'delivery' : 'pickup'}
        </LinkButton>
      )}
    </section>
  );
};

export default GettingVehicle;
